import React, { useEffect, useState, useCallback } from 'react';
import { Checkbox, Form, message, Table, Tooltip, Typography } from 'antd';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { apiUrl } from '../../common/url';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Configure dayjs plugins
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

type FormItemType =
  | 'text'
  | 'number'
  | 'decimal'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'selection'
  | 'multi-selection'
  | 'textarea';

interface Validation {
  min?: number;
  max?: number;
  pattern?: string;
  maxLength?: number;
}

interface CategoryColumn {
  name: string;
  description?: string;
  type: FormItemType;
  options?: Array<string | number>;
  isRequired?: boolean;
  validation?: Validation;
}

interface CategoryData {
  title: string;
  description: string;
  columns: CategoryColumn[];
  data: Record<string, any>[]; // Each data item is a key-value object
}

export interface BackendResponse {
  fixedColumns: {
    Key: number;
    [key: string]: any;
  }[];
  categories: CategoryData[];
}

interface PortfolioDataProps {
  client: { id: string; name?: string; token: string; permissions: string[] };
  dataCollYear: number;
}

const PortfolioData: React.FC<PortfolioDataProps> = ({ client, dataCollYear }) => {
  const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<ColumnsType<Record<string, any>>>([]);
  const [totalWidth, setTotalWidth] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchDataColl = useCallback(() => {
    if (!dataCollYear) return;
    setLoading(true);
    axios
      .get(`${apiUrl}/b2zero/${client.id}/get_data_coll`, {
        params: {
          year: dataCollYear,
        },
        headers: { Authorization: `Bearer ${client.token}` },
      })
      .then((response) => {
        const { fixedColumns, categories } = response.data as BackendResponse;
        const fixedColumnsDef: ColumnsType<Record<string, any>> = Object.keys(fixedColumns[0]).map(
          (key, index) => ({
            title: key,
            dataIndex: key,
            key: `fixed-${key}-${index}`,
            fixed: 'left' as 'left',
            width: key === 'Key' ? 75 : 200,
          })
        );

        const categoryColumns: ColumnsType<Record<string, any>> = categories.map((category) => ({
          title: <Tooltip title={category.description}>{category.title}</Tooltip>,
          key: category.title,
          children: category.columns.map((col) => ({
            title: <Tooltip title={col.description}>{col.name}</Tooltip>,
            dataIndex: col.name,
            key: `${category.title}-${col.name}`,
          })),
        }));

        setColumns([...fixedColumnsDef, ...categoryColumns]);
        setData(
          fixedColumns.map((fixedCol, fixedColIndex) => ({
            ...fixedCol,
            key: `fixedCol-${fixedColIndex}`,
            ...categories.reduce((acc, category) => {
              const categoryData = category.data.find((data) => data.Key === fixedCol.Key);
              return { ...acc, ...categoryData };
            }, {}),
          }))
        );
        const visibleCategoriesNew = categories.length > 0 ? [categories[0].title] : [];
        setVisibleCategories(visibleCategoriesNew);
        setTotalWidth(calculateTotalWidth(visibleCategoriesNew));
      })
      .catch((error) => {
        message.error('Fehler beim Abruf der Portfoliodaten: ' + error.message);
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setExtra('hint', 'Fehler beim Abrufen der Portfoliodaten: ' + error.message);
          Sentry.captureException(error);
        });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, client.token, dataCollYear]);

  useEffect(() => {
    if (dataCollYear) {
      fetchDataColl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDataColl]);

  useEffect(() => {
    setTotalWidth(calculateTotalWidth(visibleCategories));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCategories, columns]);

  const filteredColumns = columns.filter((col) =>
    'children' in col ? visibleCategories.includes(col.key as string) : true
  );

  const calculateTotalWidth = (visibleCategories: string[]) => {
    const fixedColumnsWidth = columns
      .filter((col) => !('children' in col))
      .reduce((acc, col) => {
        if (col.title === 'key') {
          return acc + 75;
        }
        return acc + 200;
      }, 0);

    const visibleCategoryColumnsWidth = columns
      .filter((col) => 'children' in col && visibleCategories.includes(col.key as string))
      .reduce((acc, col) => {
        if ('children' in col) {
          return acc + (col.children?.length || 0) * 200;
        }
        return acc;
      }, 0);

    return fixedColumnsWidth + visibleCategoryColumnsWidth;
  };

  return (
    <Table
      columns={filteredColumns}
      dataSource={data}
      rowKey="Key"
      bordered
      loading={loading}
      scroll={{ x: totalWidth }}
      title={() => (
        <>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Portfoliodaten für Bezugsjahr {dataCollYear}
          </Typography.Title>
          <Form.Item label="Kategorien" style={{ marginBottom: 0 }}>
            <Checkbox.Group
              options={columns
                .filter((col) => 'children' in col)
                .map((col, index) => ({
                  label: col.title as React.ReactNode,
                  value: col.key as string,
                  key: `checkbox-${index}`,
                }))}
              value={visibleCategories}
              onChange={(checkedValues) => {
                setVisibleCategories(checkedValues as string[]);
                setTotalWidth(calculateTotalWidth(checkedValues));
              }}
            />
          </Form.Item>
        </>
      )}
    />
  );
};

export default PortfolioData;
